<template>
  <Scroller>
    <!-- Static sections -->
    <transition name="slide-fade" mode="out-in">
      <ScrollerSection sticky="center" id="intro" v-if="!active">
        <div>
          <h1 class="f-title-5">When does oil demand reach its peak?</h1>
          <ScrollIcon>Keep scrolling to read more</ScrollIcon>
        </div>
      </ScrollerSection>
      <ScrollerSection
        sticky="left"
        behind
        id="oilPeakChart"
        key="oilPeakChart"
        v-if="['oilPeakNZE', 'oilPeakAPS', 'oilPeakSTEPS'].includes(active)"
      >
        <div class="chart__container">
          <StagedChart
            :baseOptions="oilPeakBase"
            :stages="oilPeak"
            :stage="
              ['oilPeakNZE', 'oilPeakAPS', 'oilPeakSTEPS'].indexOf(active)
            "
            class="oilPeak"
          />
        </div>
      </ScrollerSection>
      <ScrollerSection
        sticky="right"
        id="demandCircleChart"
        key="demandCircleChart"
        v-if="Object.keys(oilSavings).includes(active)"
      >
        <CircleScenarios
          key="oilSavings"
          title="Oil demand"
          :label="oilSavings[active].title"
          :historic="oilSavings[active].data[0]"
          :STEPS="oilSavings[active].data[1]"
          :APS="oilSavings[active].data[2]"
          :NZE="oilSavings[active].data[3]"
          units="mb/d"
          :max="30"
        />
      </ScrollerSection>
      <ScrollerSection
        sticky="left"
        id="oilGap"
        key="oilGap"
        behind
        v-if="active === 'newSupply'"
      >
        <img
          src="https://iea.imgix.net/1a850052-38ea-429b-8b35-ada930ea019a/oil-supply_new-supply.svg"
          :style="{ height: '100%' }"
        />
      </ScrollerSection>
    </transition>
    <!-- Normal sections -->
    <!--
    <ScrollerSection
      id="oilPeakNZE"
      tall
      @enter="handleEnter"
      @leave="handleLeave"
    >
      <Block>
        <p>
          In the
          <strong>Net Zero Emissions by 2050 Scenario (NZE)</strong>
          global action to cut emissions means oil demand never returns to its
          2019 level.
        </p>
      </Block>
    </ScrollerSection>
    -->
    <ScrollerSection
      id="oilPeakAPS"
      tall
      @enter="handleEnter"
      @leave="handleLeave"
    >
      <Block>
        <p>
          Under the <strong>Announced Pledges Scenario (APS)</strong> countries'
          net zero commitments lead to a peak in oil demand in 2024...
        </p>
      </Block>
    </ScrollerSection>
    <ScrollerSection id="oilPeakSTEPS" tall @enter="handleEnter">
      <Block>
        <p>
          ...while in the
          <strong>Stated Policies Scenario (STEPS)</strong> scenario global oil
          demand rebounds despite high prices, peaking and leveling off after
          2035.
        </p>
      </Block>
    </ScrollerSection>
    <ScrollerSection id="roadPassenger" tall @enter="handleEnter">
      <LeftBlock title="The peak in oil demand rests on changes in transport">
        <p>
          In the <strong>NZE Scenario</strong>, oil use in passenger cars falls
          sharply by 2050, when nearly all cars on the road are electric.
          Announced pledges in the APS already make a dent.
        </p>
      </LeftBlock>
    </ScrollerSection>
    <ScrollerSection id="roadFreight" @enter="handleEnter">
      <LeftBlock>
        <p>
          Road freight accounts for more than 15% of global oil demand today.
          Rising demand for freight transport causes oil demand to rise by 1.5
          mb/d to 2030 in the <strong>STEPS</strong> and by 0.3 mb/d in the
          <strong>APS</strong> despite increases in the uptake of electric and
          fuel cell heavy-duty trucks and liquid biofuels.
        </p>
        <p>
          In the <strong>NZE Scenario</strong>, nearly all trucks sold from 2040
          use electricity or hydrogen, leading to a strong decline in oil
          demand.
        </p>
      </LeftBlock>
    </ScrollerSection>
    <ScrollerSection id="aviationShipping" @enter="handleEnter">
      <LeftBlock>
        <p>
          Oil demand in aviation and shipping will continue to increase, driven
          by economic growth, without stronger action on alternative fuels and
          beahviour changes.
        </p>
      </LeftBlock>
    </ScrollerSection>
    <ScrollerSection id="petrochemicals" @enter="handleEnter">
      <LeftBlock>
        <p>
          The chemicals sector is set to account for a rising share of oil use
          in each scenario. Oil is mostly used in this sector as a feedstock for
          making plastics. In the <strong>APS</strong> and <strong>NZE</strong>,
          growing rates of plastic recylcing help to moderate the increase in
          oil demand.
        </p>
      </LeftBlock>
    </ScrollerSection>
    <ScrollerSection id="newSupply" @enter="handleEnter">
      <Block>
        <h4 class="f-title-5">What new supplies are needed?</h4>
        <p>
          Oil demand increases by 8 mb/d in the STEPS and falls by 1.5 mb/d in
          the APS between 2021 and 2030. Production from existing fields drops
          by 18 mb/d over this period, leaving a large gap in 2030 that needs to
          be filled by new sources of supply.
        </p>
      </Block>
    </ScrollerSection>
    <ScrollerSection id="fin" @enter="handleEnter" />
  </Scroller>
</template>

<script>
import {
  Block,
  Scroller,
  //ScrollerImage,
  //ImageCaption,
  ScrollerSection,
  //Parallax,
  ScrollIcon,
} from "@international-energy-agency/vue-ui";
import LeftBlock from "../components/LeftBlock.vue";
import CircleScenarios from "../components/CircleScenarios.vue";
import StagedChart from "../components/StagedChart.vue";
import ActiveMixin from "../mixins/active";
export default {
  components: {
    Block,
    LeftBlock,
    Scroller,
    ScrollerSection,
    //Parallax,
    //ScrollerImage,
    //ImageCaption,
    ScrollIcon,
    CircleScenarios,
    StagedChart,
  },
  mixins: [ActiveMixin],
  data() {
    return {
      oilPeakBase: {
        chart: {
          type: "area",
          height: 400,
          margin: [0, 1, 20, 1],
        },
        plotOptions: {
          area: {
            stacking: true,
          },
          series: {
            states: {
              hover: {
                enabled: false,
              },
            },
            animation: false,
          },
        },
        legend: {
          enabled: false,
        },
        tooltip: {
          enabled: false,
        },
        exporting: {
          enabled: false,
        },
        responsive: {
          rules: [
            {
              condition: {
                maxWidth: 540,
              },
              chartOptions: {
                yAxis: {
                  labels: {
                    enabled: false,
                  },
                },
              },
            },
          ],
        },
      },
      oilPeakStageOptions: [
        {
          year: 2021,
          yPlotLine: {
            /*
            value: 97.7,
            className: "highcharts-dotted",
            zIndex: 99,
            label: {
              text: "97.7 mb/d",
              align: "right",
              x: -8,
            },
            */
          },
          xPlotLine: {
            /*
            value: 2019,
            label: {
              useHTML: true,
              text: "<span class='oilPeak-label__year'>2019</span><br><span class='oilPeak-label__scenario color-NZE'>NZE</span>",
              rotation: 0,
              verticalAlign: "top",
            },
            zIndex: 99,
            */
          },
        },
        {
          year: 2024,
          yPlotLine: {
            value: 98.1,
            className: "highcharts-dotted",
            zIndex: 99,
            label: {
              text: "98.1 mb/d",
              align: "right",
              x: -8,
            },
          },
          xPlotLine: {
            value: 2024,
            label: {
              useHTML: true,
              text: "<span class='oilPeak-label__year'>2024</span><br><span class='oilPeak-label__scenario color-APS'>APS</span>",
              rotation: 0,
              verticalAlign: "top",
            },
            zIndex: 99,
          },
        },
        {
          year: 2040,
          yPlotLine: {
            value: 103.2,
            className: "highcharts-dotted",
            zIndex: 99,
            label: {
              text: "103.2 mb/d",
              align: "right",
              x: -8,
            },
          },
          xPlotLine: {
            value: 2035,
            label: {
              useHTML: true,
              text: "<span class='oilPeak-label__year'>2035</span><br><span class='oilPeak-label__scenario color-STEPS'>STEPS</span>",
              rotation: 0,
              verticalAlign: "top",
            },
            zIndex: 99,
          },
        },
      ],
      oilPeakData: {
        historic: [
          [2015, 93.4],
          [2016, 94.1],
          [2017, 96.1],
          [2018, 97.2],
          [2019, 97.7],
          [2020, 88.9],
          [2021, 94.5],
        ],
        NZE: [
          [2021, 94.5],
          [2022, 95.9],
          [2023, 96.3],
          [2024, 94.5],
          [2025, 91.9],
          [2026, 89.0],
          [2027, 85.7],
          [2028, 82.4],
          [2029, 78.9],
          [2030, 75.3],
          [2031, 71.5],
          [2032, 67.7],
          [2033, 64.1],
          [2034, 60.5],
          [2035, 56.9],
          [2036, 53.4],
          [2037, 50.2],
          [2038, 47.1],
          [2039, 44.2],
          [2040, 41.4],
        ],
        APS: [
          [2021, 0.0],
          [2022, 0.1],
          [2023, 1.4],
          [2024, 3.5],
          [2025, 6.0],
          [2026, 8.2],
          [2027, 10.6],
          [2028, 13.0],
          [2029, 15.4],
          [2030, 17.7],
          [2031, 19.9],
          [2032, 21.9],
          [2033, 23.7],
          [2034, 25.2],
          [2035, 26.7],
          [2036, 27.9],
          [2037, 29.0],
          [2038, 30.0],
          [2039, 30.8],
          [2040, 31.5],
        ],
        STEPS: [
          [2021, 0.0],
          [2022, 0.2],
          [2023, 0.6],
          [2024, 1.4],
          [2025, 2.5],
          [2026, 3.5],
          [2027, 4.9],
          [2028, 6.3],
          [2029, 7.8],
          [2030, 9.4],
          [2031, 11.3],
          [2032, 13.3],
          [2033, 15.3],
          [2034, 17.5],
          [2035, 19.7],
          [2036, 21.8],
          [2037, 24.0],
          [2038, 26.0],
          [2039, 28.1],
          [2040, 29.9],
        ],
      },
      oilSavings: {
        petrochemicals: {
          title: "Petrochemicals",
          data: [14, 19, 15, 13],
        },
        roadPassenger: {
          title: "Road passenger",
          data: [25, 21, 16, 0.4],
        },
        roadFreight: {
          title: "Road freight",
          data: [15, 18, 8, 1],
        },
        aviationShipping: {
          title: "Aviation and shipping",
          data: [10, 18, 10, 2],
        },
      },
    };
  },
  computed: {
    oilPeak() {
      const { NZE, APS, STEPS, historic } = this.oilPeakData;
      return this.oilPeakStageOptions.map((stage) => ({
        xAxis: {
          min: 2015,
          max: 2040,
          labels: { enabled: true },
          tickPositions: [2015, 2040],
          tickLength: 5,
          plotLines: [
            /*
            {
              value: 2015,
              label: {
                useHTML: true,
                text: "<span class='oilPeak-label__year'>2015</span>",
                rotation: 0,
                verticalAlign: "top",
              },
              zIndex: 99,
            },
            */
            {
              value: 2021,
              label: {
                useHTML: true,
                text: "<span class='oilPeak-label__year'>2021</span>",
                rotation: 0,
                verticalAlign: "top",
              },
              zIndex: 99,
            },
            ...this.oilPeakStageOptions
              .filter((s) => s.year < stage.year)
              .map((s) => s.xPlotLine),
            stage.xPlotLine,
            /*
            {
              value: 2040,
              label: {
                useHTML: true,
                text: "<span class='oilPeak-label__year'>2040</span>",
                rotation: 0,
                x: -6,
                align: "left",
                textAlign: "right",
              },
              zIndex: 99,
            },
            */
          ],
        },
        yAxis: {
          max: 125,
          plotLines: [
            {
              value: 0,
              className: "highcharts-zero-axis",
              zIndex: 99,
            },
            stage.yPlotLine,
          ],
          opposite: true,
          labels: {
            formatter: function () {
              return this.isLast ? "mb/d" : this.value;
            },
          },
          tickPositions: [0, 43, 74, 104, 125],
        },
        series: [
          {
            id: "Historic",
            name: "Historic",
            zIndex: 4,
            data: historic,
            className: "oilPeak-area color-historic",
          },
          {
            id: "NZE",
            name: "NZE",
            zIndex: 3,
            data: NZE.filter((point) => point[0] <= stage.year),
            className: "oilPeak-area color-NZE",
            label: { enabled: true, className: "color-NZE" },
            /*
            zoneAxis: "x",
            zones: [
              {
                value: 2023,
              },
              {
                className: "oilPeak-area--peaked",
              },
            ],
            */
          },
          {
            id: "APS",
            name: "APS",
            zIndex: 2,
            data: APS.filter((point) => point[0] <= stage.year),
            className: "oilPeak-area color-APS",
            label: { enabled: true, className: "color-APS" },
            /*
            zoneAxis: "x",
            zones: [
              {
                value: 2025,
              },
              {
                className: "oilPeak-area--peaked",
              },
            ],
            */
          },
          {
            id: "STEPS",
            name: "STEPS",
            zIndex: 1,
            data: STEPS.filter((point) => point[0] <= stage.year),
            className: "oilPeak-area color-STEPS",
            label: { enabled: true, className: "color-STEPS" },
            /*
            zoneAxis: "x",
            zones: [
              {
                value: 2035,
              },
              {
                className: "oilPeak-area--peaked",
              },
            ],
            */
          },
        ],
      }));
    },
  },
};
</script>

<style>
@import url("../css/main.css");
/* Oil peak chart styles*/
.oilPeak {
  width: 100%;
}
.highcharts-grid-line {
  stroke: #f4f4f4;
}
.highcharts-grid-line:last-child {
  stroke: none;
}
.oilPeak-area.color-NZE path.oilPeak-area--peaked {
  fill: #c8e7a7 !important;
}
.oilPeak-area.color-APS path.oilPeak-area--peaked {
  fill: #ffdf7f !important;
}
.oilPeak-area.color-STEPS path.oilPeak-area--peaked {
  fill: #a4d5e2 !important;
}
.oilPeak-area path {
  fill-opacity: 0.66;
  stroke-width: 0;
}
.oilPeak-label__year {
  font-size: 16px;
  font-weight: 600;
}
.oilPeak-label__scenario {
  font-size: 20px;
  font-weight: 600;
}
.highcharts-plot-line {
  stroke: #000;
}
.color-STEPS text {
  fill: #4bacc6;
}
.color-APS text {
  fill: #ffc000;
}
.color-NZE text {
  fill: #92d050;
}
@media screen and (max-width: 860px) {
  .oilPeak-label__year {
    font-size: 10px !important;
  }
  .oilPeak-label__scenario {
    font-size: 14px !important;
  }
}
</style>
